export class Crystal {
    constructor(color, size = 1, isRevealed = false) {
      this.color = color;
      this.size = size;
      this.isRevealed = isRevealed;
    }
  }
  
  
  export class Random {
    constructor(seed) {
      this.seed = seed;
    }
  
    next() {
      this.seed = (this.seed * 16807) % 2147483647;
      return (this.seed - 1) / 2147483646;
    }
  
    nextInt(min, max) {
      return Math.floor(this.next() * (max - min)) + min;
    }
  
    shuffle(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(this.next() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    }
  }

  // Configuration for challenge frequencies
  const CHALLENGE_CONFIG = {
    EXTENDED_TUBE_FREQUENCY: 2,  // Every 2nd level
    HIDDEN_PIECES_FREQUENCY: 3,  // Every 3rd level
    GRAVITY_FREQUENCY: 2,        // Every 2nd level, offset by 1
    TIME_LIMIT_FREQUENCY: 3,     // Every 3th level, offset by 1
    BASE_TIME_LIMIT: 30,         // Base time in seconds per color
    TIME_LIMIT_SCALE: 1,       // Time reduction factor per level
};
  
  // Export challenge check functions with proper hardMode checks
  export function isExtendedTubeLevel(level, isHardMode = false) {
    if (!isHardMode) return false;  // Normal mode never has extended tubes
    return level % CHALLENGE_CONFIG.EXTENDED_TUBE_FREQUENCY === 0;
  }
  
  export function getTubeHeight(level, isHardMode = false) {
    if (!isHardMode) return 4;  // Normal mode always has height 4
    return isExtendedTubeLevel(level, isHardMode) ? 5 : 4;
  }
  
  export function hasHiddenPieces(level, isHardMode = false) {
    if (!isHardMode) return false;  // Normal mode never has hidden pieces
    return level % CHALLENGE_CONFIG.HIDDEN_PIECES_FREQUENCY === 0;
  }
  
  export function hasGravityMode(level, isHardMode = false) {
    if (!isHardMode) return false;  // Normal mode never has gravity
    return (level + 1) % CHALLENGE_CONFIG.GRAVITY_FREQUENCY === 0;
  }

  export function hasTimeLimit(level, isHardMode = false) {
    if (!isHardMode) return false;  // Normal mode never has time limit
    return (level + 1)% CHALLENGE_CONFIG.TIME_LIMIT_FREQUENCY === 0;
}

export function calculateTimeLimit(level, numColors) {
    // Add defensive check
    if (numColors <= 0) {
        console.error('Invalid number of colors:', numColors);
        numColors = getColorsForLevel(level); // Use the level generation function to get expected colors
    }
    
    const baseTime = CHALLENGE_CONFIG.BASE_TIME_LIMIT * numColors;
    const levelFactor = Math.pow(CHALLENGE_CONFIG.TIME_LIMIT_SCALE, 
        Math.floor(level / CHALLENGE_CONFIG.TIME_LIMIT_FREQUENCY));
    
    return Math.max(Math.floor(baseTime * levelFactor), numColors * 10);
}
  
  // Helper function to preview challenge distribution
  export function previewChallengeDistribution(startLevel, endLevel, isHardMode = false) {
    const preview = [];
    for (let level = startLevel; level <= endLevel; level++) {
      const activeModifiers = [];
      
      if (isExtendedTubeLevel(level, isHardMode)) activeModifiers.push('Extended');
      if (hasHiddenPieces(level, isHardMode)) activeModifiers.push('Hidden');
      if (hasGravityMode(level, isHardMode)) activeModifiers.push('Gravity');
      
      preview.push({
        level,
        modifiers: activeModifiers.length ? activeModifiers.join('+') : 'Normal',
        tubeHeight: getTubeHeight(level, isHardMode)
      });
    }
    return preview;
  }

  //const preview = previewChallengeDistribution(1, 20, true);
  //console.log(preview);
  
  export function getColorsForLevel(level) {
    // Levels 1-12: Increase by 1 color every 3 levels, starting at 2 colors
    if (level <= 12) {
      // Math.floor((level - 1) / 3) gives us 0, 0, 0, 1, 1, 1, 2, 2, 2, 3, 3, 3
      // Adding 2 as level 1 has 2 colors at the start
      return Math.min(6, Math.floor((level - 1) / 3) + 2);
    }
    
    // Levels 13+: Increase by 1 color every 6 levels
    // First, calculate how many additional colors after the base 6
    const additionalColors = Math.floor((level - 13) / 10) + 1;
    
    // Return total colors (base 6 + additional), capped at 25
    return Math.min(50, 6 + additionalColors);
  }
  
  export function generateLevel(level) {
    const seed = level * 42857143; // New seed for normal mode
    const random = new Random(seed);
    
    const numColors = getColorsForLevel(level);
    const currentTubeHeight = getTubeHeight(level, false);
    
    let emptyTubes;
    if (numColors <= 7) {
      emptyTubes = 2;
    } else if (numColors <= 12) {
      emptyTubes = 3;
    } else {
      emptyTubes = 4;
    }
    
    const numTubes = numColors + emptyTubes;
    let tubes = Array(numTubes).fill().map(() => []);
    
    let segments = [];
    for (let color = 0; color < numColors; color++) {
      for (let i = 0; i < currentTubeHeight; i++) {
        // In normal mode, all pieces are always revealed
        segments.push(new Crystal(color, 1, true));
      }
    }
    
    segments = random.shuffle(segments);
    
    let segmentIndex = 0;
    for (let tubeIndex = 0; tubeIndex < numTubes - emptyTubes; tubeIndex++) {
      for (let i = 0; i < currentTubeHeight; i++) {
        tubes[tubeIndex].push(segments[segmentIndex++]);
      }
    }
    
    return combineColors(tubes);
  }
  
  export function isValidMove(tubes, fromIdx, toIdx, level, isHardMode = false) {
    if (fromIdx === toIdx) return false;
    
    const fromTube = tubes[fromIdx];
    const toTube = tubes[toIdx];
    
    if (!fromTube || fromTube.length === 0) return false;
    
    const maxHeight = getTubeHeight(level, isHardMode);
    const movingCrystal = fromTube[fromTube.length - 1];
    
    // Calculate current height of target tube
    const currentHeight = toTube ? toTube.reduce((sum, crystal) => sum + crystal.size, 0) : 0;
    
    // Check if adding the new crystal would exceed max height
    if (currentHeight + movingCrystal.size > maxHeight) {
        return false;
    }
    
    // Empty tube is always valid if we have space
    if (!toTube || toTube.length === 0) return true;
    
    // Check if we're in gravity mode
    if (isHardMode && hasGravityMode(level, isHardMode)) {
        // In gravity mode, check bottom piece
        const bottomCrystal = toTube[0];
        return bottomCrystal.color === movingCrystal.color;
    } else {
        // Normal mode - check top piece
        const topCrystal = toTube[toTube.length - 1];
        return topCrystal.color === movingCrystal.color;
    }
}
  
  export function isValidHardModeMove(tubes, fromIdx, toIdx, level) {
    return isValidMove(tubes, fromIdx, toIdx, level, true);
  }
  
  export function combineColors(tubes) {
    return tubes.map(tube => {
      if (tube.length <= 1) return tube;
      
      const combined = [];
      let currentGroup = null;
      
      for (const crystal of tube) {
        if (!currentGroup || currentGroup.color !== crystal.color) {
          // Use the crystal's current reveal state
          currentGroup = new Crystal(crystal.color, crystal.size, crystal.isRevealed);
          combined.push(currentGroup);
        } else {
          currentGroup.size += crystal.size;
          // If any piece in the group is revealed, the whole group becomes revealed
          currentGroup.isRevealed = currentGroup.isRevealed || crystal.isRevealed;
        }
      }
      
      return combined;
    });
}
  
  export function isSolved(tubes, level, isHardMode = false) {
    const maxHeight = getTubeHeight(level, isHardMode);
    
    const uniqueColors = new Set();
    for (const tube of tubes) {
      if (tube.length > 0) {
        uniqueColors.add(tube[0].color);
      }
    }
    const numColors = uniqueColors.size;
    
    let completedTubes = 0;
    const seenColors = new Set();
    
    for (const tube of tubes) {
      if (tube.length === 0) continue;
      
      const totalSize = tube.reduce((sum, crystal) => sum + crystal.size, 0);
      
      if (totalSize === maxHeight) {
        const tubeColor = tube[0].color;
        const isComplete = tube.every(crystal => crystal.color === tubeColor);
        
        if (isComplete && !seenColors.has(tubeColor)) {
          completedTubes++;
          seenColors.add(tubeColor);
        }
      }
    }
    
    return completedTubes === numColors && numColors > 0;
  }
  
export function generateHardModeLevel(level) {
    const seed = level * 73951864 + 29387; // New seed for challenge/hard mode
    const random = new Random(seed);
    const numColors = getColorsForLevel(level);
    const currentTubeHeight = getTubeHeight(level, true);
    const includeHiddenPieces = hasHiddenPieces(level, true);
    
    let emptyTubes;
    if (numColors <= 7) {
      emptyTubes = 2;
    } else if (numColors <= 12) {
      emptyTubes = 3;
    } else {
      emptyTubes = 4;
    }
    
    const numTubes = numColors + emptyTubes;
    let tubes = Array(numTubes).fill().map(() => []);
    
    let segments = [];
    for (let color = 0; color < numColors; color++) {
      for (let i = 0; i < currentTubeHeight; i++) {
        // All pieces start hidden in hard mode
        segments.push(new Crystal(color, 1, false));
      }
    }
    
    segments = random.shuffle(segments);
    
    let segmentIndex = 0;
    for (let tubeIndex = 0; tubeIndex < numTubes - emptyTubes; tubeIndex++) {
      for (let i = 0; i < currentTubeHeight; i++) {
        tubes[tubeIndex].push(segments[segmentIndex++]);
      }
    }
    
    const combinedTubes = combineColors(tubes);
    return updateRevealedState(combinedTubes, level, true);
}

export function generateTeamModeLevel(level) {
    // Use a completely different seed for team mode to generate entirely new levels
    const seed = level * 15926535 + 89793; // New unique seed for team mode
    const random = new Random(seed);
    
    const numColors = getColorsForLevel(level);
    const currentTubeHeight = 4; // Team mode uses normal tube height
    
    let emptyTubes;
    if (numColors <= 7) {
      emptyTubes = 2;
    } else if (numColors <= 12) {
      emptyTubes = 3;
    } else {
      emptyTubes = 4;
    }
    
    const numTubes = numColors + emptyTubes;
    let tubes = Array(numTubes).fill().map(() => []);
    
    let segments = [];
    for (let color = 0; color < numColors; color++) {
      for (let i = 0; i < currentTubeHeight; i++) {
        // Team mode pieces are always revealed (like normal mode)
        segments.push(new Crystal(color, 1, true));
      }
    }
    
    segments = random.shuffle(segments);
    
    let segmentIndex = 0;
    for (let tubeIndex = 0; tubeIndex < numTubes - emptyTubes; tubeIndex++) {
      for (let i = 0; i < currentTubeHeight; i++) {
        tubes[tubeIndex].push(segments[segmentIndex++]);
      }
    }
    
    return combineColors(tubes);
}

// New function to update revealed state
export function updateRevealedState(tubes, level, isHardMode) {
    if (!isHardMode || !hasHiddenPieces(level, isHardMode)) {
        // In normal mode or levels without hidden pieces, reveal everything
        return tubes.map(tube => 
            tube.map(crystal => ({ ...crystal, isRevealed: true }))
        );
    }

    // In hard mode with hidden pieces, only reveal top pieces
    return tubes.map(tube => {
        if (tube.length === 0) return tube;
        
        return tube.map((crystal, index) => ({
            ...crystal,
            isRevealed: index === tube.length - 1 // Only reveal if it's the top piece
        }));
    });
}
